import { useState } from "react";
import { useNavigate } from "react-router";
import { useDashboard } from "../hooks/useDashboard";
import { useMyActivity } from "../hooks/useMyActivity";
import { ExportStatusCard } from "../components/ExportStatusCard";
import { OnboardingChecklist } from "../components/OnboardingChecklist";
import { UploadDialog } from "../../batches/components/UploadDialog";
import { useJobTypes } from "../../admin/hooks/useJobTypes";
import "./DashboardHubPage.css";

// Step color palette for pipeline bar.
// Values are CSS custom-property references so the palette follows the
// active theme. Consumers pass the result into `style={{ background: ... }}`.
const STEP_COLORS: Record<string, string> = {
  scan: "var(--chart-series-2)",
  key: "var(--color-success)",
  verify: "var(--chart-series-4)",
  qc: "var(--color-warning)",
  export: "var(--chart-series-2)",
  // fallback handled below
};

function stepColor(step: string): string {
  const normalized = step.toLowerCase();
  for (const [key, color] of Object.entries(STEP_COLORS)) {
    if (normalized.includes(key)) return color;
  }
  return "var(--color-text-muted)";
}

function formatDuration(ms: number): string {
  if (ms === 0) return "—";
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.round(seconds / 60);
  return `${minutes}m`;
}

export function DashboardHubPage() {
  const navigate = useNavigate();
  const [uploadOpen, setUploadOpen] = useState(false);
  const { fleet, isLoading: fleetLoading, isRefreshing: fleetRefreshing, error: fleetError, refresh } = useDashboard();
  // R-182 — initial-load skeleton only. `fleetLoading` is one-way (the 30s
  // poll + manual refresh never re-flip it true), so gate the Workload skeleton
  // on data-presence so a background refresh keeps the fleet grid mounted.
  const showFleetSkeleton = fleetLoading && !fleet;
  const { activity, isLoading: activityLoading } = useMyActivity();
  // P2-2: The fleet endpoint only counts JTs with ACTIVE DOCUMENTS, so a fresh
  // tenant with N configured JTs and no documents would have `fleet.jobTypes` empty
  // and the onboarding card would never auto-dismiss. Source the count from the
  // real JT list endpoint instead.
  const { total: jobTypeTotal } = useJobTypes();

  const hasBreach = (fleet?.totalSlaBreaches ?? 0) > 0;

  return (
    <div className="dashboard-page form-default">
      {/* Header */}
      <div className="dashboard-header">
        <h1 className="dashboard-title">
          Skarn <span>Capture</span>
        </h1>

        {fleet && (
          <div className="dashboard-summary">
            <div className="dashboard-summary-stat">
              <span className="dashboard-summary-value">{fleet.totalDocuments}</span>
              <span className="dashboard-summary-label">Active Docs</span>
            </div>
            <div className="dashboard-summary-stat">
              <span className="dashboard-summary-value">{fleet.totalOperators}</span>
              <span className="dashboard-summary-label">Operators</span>
            </div>
            <div className="dashboard-summary-stat">
              <span className={`dashboard-summary-value ${hasBreach ? "dashboard-summary-value--breach" : ""}`}>
                {fleet.totalSlaBreaches}
              </span>
              <span className="dashboard-summary-label">SLA Breaches</span>
            </div>
          </div>
        )}

        {/* R-182 — `disabled`/label reflect the background refetch so the
            manual refresh button gives feedback without blanking the page. */}
        <button
          className="dashboard-refresh-btn"
          onClick={refresh}
          disabled={fleetRefreshing}
          title="Refresh data"
        >
          ↻ {fleetRefreshing ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {/* Body */}
      <div className="dashboard-body">
        {/* Fresh-tenant onboarding — self-hides once jobTypeCount > 0. */}
        {!showFleetSkeleton && !fleetError && fleet && (
          <OnboardingChecklist jobTypeCount={jobTypeTotal} />
        )}

        {/* Top row: 2 widgets */}
        <div className="dashboard-grid-2">
          {/* My Activity */}
          <div className="dashboard-widget">
            <p className="dashboard-widget-title">My Activity Today</p>
            {activityLoading ? (
              <div className="dashboard-loading">
                <div className="loading-spinner" />
              </div>
            ) : activity ? (
              <div className="activity-stats">
                <div className="activity-stat">
                  <span className="activity-stat-value">{activity.docsToday}</span>
                  <span className="activity-stat-label">Docs Processed</span>
                </div>
                <div className="activity-stat">
                  <span className="activity-stat-value">{formatDuration(activity.avgDurationMs)}</span>
                  <span className="activity-stat-label">Avg per Doc</span>
                </div>
              </div>
            ) : (
              <p className="activity-empty">No activity recorded today.</p>
            )}
          </div>

          {/* Quick Actions */}
          <div className="dashboard-widget">
            <p className="dashboard-widget-title">Quick Actions</p>
            <div className="quick-actions">
              <button
                className="quick-action-btn"
                onClick={() => setUploadOpen(true)}
              >
                <span className="quick-action-icon">+</span>
                Upload Batch
              </button>
              <button
                className="quick-action-btn"
                onClick={() => navigate("/queues")}
              >
                <span className="quick-action-icon">⚡</span>
                Go to Queues
              </button>
              <button
                className="quick-action-btn"
                onClick={() => navigate("/monitor")}
              >
                <span className="quick-action-icon">📊</span>
                Live Monitor
              </button>
              <button
                className="quick-action-btn"
                onClick={() => navigate("/admin/analytics")}
              >
                <span className="quick-action-icon">📈</span>
                Analytics
              </button>
            </div>
          </div>
        </div>

        {/* Second row: Export Status */}
        <div className="dashboard-grid-2">
          <ExportStatusCard />
        </div>

        {/* Workload Overview (full width) */}
        <div className="dashboard-widget dashboard-widget--full">
          <p className="dashboard-widget-title">Workload Overview</p>

          {showFleetSkeleton && (
            <div className="dashboard-loading">
              <div className="loading-spinner" />
            </div>
          )}

          {fleetError && (
            <div className="dashboard-error">Error: {fleetError}</div>
          )}

          {!showFleetSkeleton && !fleetError && fleet && fleet.jobTypes.length === 0 && (
            <div className="dashboard-empty">No active documents in the queue.</div>
          )}

          {!showFleetSkeleton && !fleetError && fleet && fleet.jobTypes.length > 0 && (
            <div className="fleet-grid">
              {fleet.jobTypes.map((jt) => {
                const totalStepDocs = jt.steps.reduce((sum, s) => sum + s.total, 0);
                const slaBadge = jt.slaBreaches === 0 ? "ok" : jt.slaBreaches < 3 ? "warn" : "breach";

                return (
                  <div
                    key={jt.id}
                    className="fleet-card"
                    onClick={() => navigate(`/monitor?jobType=${jt.id}`)}
                  >
                    <div className="fleet-card-header">
                      <span className="fleet-card-name">{jt.name}</span>
                      <span className={`sla-badge sla-badge--${slaBadge}`}>
                        {jt.slaBreaches === 0 ? "SLA OK" : `${jt.slaBreaches} breach${jt.slaBreaches !== 1 ? "es" : ""}`}
                      </span>
                    </div>

                    <div className="fleet-card-meta">
                      <div className="fleet-card-meta-item">
                        <span className="fleet-card-meta-value">{jt.totalDocuments}</span>
                        <span>docs</span>
                      </div>
                      <div className="fleet-card-meta-item">
                        <span className="fleet-card-meta-value">{jt.activeOperators}</span>
                        <span>operators</span>
                      </div>
                    </div>

                    {/* Pipeline bar */}
                    {totalStepDocs > 0 && (
                      <>
                        <div className="pipeline-bar">
                          {jt.steps.map((s) => (
                            <div
                              key={s.step}
                              className="pipeline-bar-segment"
                              style={{
                                width: `${(s.total / totalStepDocs) * 100}%`,
                                background: stepColor(s.step),
                              }}
                              title={`${s.step}: ${s.total} docs`}
                            />
                          ))}
                        </div>
                        <div className="pipeline-bar-legend">
                          {jt.steps.map((s) => (
                            <span key={s.step} className="pipeline-bar-legend-item">
                              <span
                                className="pipeline-bar-dot"
                                style={{ background: stepColor(s.step) }}
                              />
                              {s.step} ({s.total})
                            </span>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <UploadDialog open={uploadOpen} onClose={() => setUploadOpen(false)} />
    </div>
  );
}
